package com.nonopn.skydome.common.app.api.impl

import kotlinx.serialization.Serializable

// Models of the promotion codes routes (lpsmanager spec of 2026-11-02):
// - GET / POST /api/admin/promotion-codes and DELETE /api/admin/promotion-codes/{uuid} (administrators);
// - GET /api/checkout/promotion-codes/{code}?app= (user bearer).
// Every field of the API is declared. The state stays a string so that a value added later by the server never
// breaks the decoding (see PromoCodeState.parse).

/** Per currency: invoices paid by the subscriptions started with the code. Amounts in cents, without taxes. */
@Serializable
data class AdminPromotionCodeCommissions(
    val currency: String,
    val invoices: Int = 0,
    val paidAmount: Long = 0L,
    val commissionAmount: Long = 0L,
)

/** `AdminPromotionCode` of the API: what the GET lists and what the POST / DELETE answer. */
@Serializable
data class AdminPromotionCode(
    val uuid: String,
    val app: String,
    val code: String,
    val active: Boolean = true,
    // Active | Disabled | Expired | Exhausted
    val state: String = "",
    val createdAtSecondsUtc: Long = 0L,
    val percentOff: Int? = null,
    val freeMonths: Int? = null,
    val maxRedemptions: Int? = null,
    val redemptions: Int = 0,
    val validUntilSecondsUtc: Long? = null,
    val referrer: AdminAccount? = null,
    val commissionPercent: Int? = null,
    val onStripeCheckout: Boolean = false,
    val commissions: List<AdminPromotionCodeCommissions> = emptyList(),
)

/**
 * Body of POST /api/admin/promotion-codes: always the COMPLETE code. A field left out is reset by the server
 * (no limit, no end date, active again...), so every field is always sent, null included.
 */
@Serializable
data class AdminPromotionCodeRequest(
    val uuid: String?,
    val app: String,
    val code: String,
    val active: Boolean,
    val percentOff: Int?,
    val freeMonths: Int?,
    val maxRedemptions: Int?,
    val validUntilSecondsUtc: Long?,
    val referrerEmail: String?,
    val commissionPercent: Int?,
)

/** `ApiPromotionCode` of GET /api/checkout/promotion-codes/{code}: what the code gives to the account. */
@Serializable
data class ApiPromotionCode(
    val code: String,
    val percentOff: Int? = null,
    val freeMonths: Int? = null,
)

/** `ApiConflict` of the API: one refused field of an `InvalidRequest` error. */
@Serializable
data class ApiConflict(
    val field: String = "",
    val message: String = "",
)

/** `ApiError` of the API with its `conflicts` (409 InvalidRequest of POST /api/admin/promotion-codes). */
@Serializable
data class ApiErrorWithConflicts(
    val code: String = "",
    val message: String = "",
    val conflicts: List<ApiConflict>? = null,
)
